import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TimeTrackingDay } from '../GraphQL/indexV2';
import { useFlag } from '@unleash/proxy-client-react';
import { FeatureFlagEnums } from '../FeatureFlagEnums';
import { countOnCallHoursWithDates } from './onCallTimeHelpersWithDates';

interface Props {
	data: TimeTrackingDay;
}

const OnCallSummarizer: React.FC<Props> = ({ data }) => {
	const { t } = useTranslation();

	const showOnCallHours = useFlag(FeatureFlagEnums.SHOW_ONCALL_HOURS);
	const onCallSummarize = useFlag(FeatureFlagEnums.ON_CALL_SUMMARIZE);

	if (data === undefined) return <></>;

	const currentDay = data;

	const destructuredEntries =
		currentDay?.timeTrackingEntries.map(({ jobNo, jobTaskNo, hours, onCall, startTime, endTime, startDate, endDate }) => ({
			jobNo,
			jobTaskNo,
			hours,
			onCall,
			startTime,
			endTime,
			startDate,
			endDate,
		})) ?? [];

	const destructuredAddonLines =
		currentDay?.timeTrackingEntries.flatMap(e =>
			e.addonLines.map(({ paymentSupplementCode, jobNo, jobTaskNo, hours, onCall }) => ({
				paymentSupplementCode,
				jobNo,
				jobTaskNo,
				hours,
				onCall,
				startTime: e.startTime,
				endTime: e.endTime,
			})),
		) ?? [];

	const onCallHours = countOnCallHoursWithDates(data.date, destructuredEntries, destructuredAddonLines, '0601', 2.5);
	const onCallbackHours = countOnCallHoursWithDates(data.date, destructuredEntries, destructuredAddonLines, '0600', 3);
	const asbestosOnCallHours = countOnCallHoursWithDates(data.date, destructuredEntries, destructuredAddonLines, '0612', 2.5);

	return (
		<div>
			{onCallSummarize ? (
				<div className="flex flex-col justify-end items-end">
					{onCallHours.hours > 0 && (
						<p className="ml-2 font-semibold">
							<Trans
								t={t}
								i18nKey="timeRegistration.onCallHourSummary"
								count={onCallHours.hours}
								values={{
									onCallHours: onCallHours.hours,
								}}
							/>
							{onCallHours.overtimeHours > 0 && (
								<span className="ml-2 font-semibold">
									(
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallOvertimeHourSummary"
										count={onCallHours.overtimeHours}
										values={{
											onCallHours: onCallHours.overtimeHours,
										}}
									/>
									)
								</span>
							)}
							{onCallHours.eveningHours > 0 && (
								<span className="ml-2 font-semibold">
									(
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallEveningHourSummary"
										count={onCallHours.eveningHours}
										values={{
											onCallHours: onCallHours.eveningHours,
										}}
									/>
									)
								</span>
							)}
							{onCallHours.nightHours > 0 && (
								<span className="ml-2 font-semibold">
									(
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallNightHourSummary"
										count={onCallHours.nightHours}
										values={{
											onCallHours: onCallHours.nightHours,
										}}
									/>
									)
								</span>
							)}
							{onCallHours.sundayHours > 0 && (
								<span className="ml-2 font-semibold">
									(
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallSundayHourSummary"
										count={onCallHours.sundayHours}
										values={{
											onCallHours: onCallHours.sundayHours,
										}}
									/>
									)
								</span>
							)}
						</p>
					)}
					{onCallbackHours.hours > 0 && (
						<p className="ml-2 font-semibold">
							<Trans
								t={t}
								i18nKey="timeRegistration.onCallbackHourSummary"
								count={onCallbackHours.hours}
								values={{
									onCallbackHours: onCallbackHours.hours,
								}}
							/>
							{onCallbackHours.overtimeHours > 0 && (
								<span className="ml-2">
									(
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallbackOvertimeHourSummary"
										count={onCallbackHours.overtimeHours}
										values={{
											onCallbackHours: onCallbackHours.overtimeHours,
										}}
									/>
									)
								</span>
							)}
							{onCallbackHours.eveningHours > 0 && (
								<span className="ml-2 font-semibold">
									(
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallEveningHourSummary"
										count={onCallbackHours.eveningHours}
										values={{
											onCallbackHours: onCallbackHours.eveningHours,
										}}
									/>
									)
								</span>
							)}
							{onCallbackHours.nightHours > 0 && (
								<span className="ml-2 font-semibold">
									(
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallNightHourSummary"
										count={onCallbackHours.nightHours}
										values={{
											onCallbackHours: onCallbackHours.nightHours,
										}}
									/>
									)
								</span>
							)}
							{onCallbackHours.sundayHours > 0 && (
								<span className="ml-2 font-semibold">
									(
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallSundayHourSummary"
										count={onCallbackHours.sundayHours}
										values={{
											onCallbackHours: onCallbackHours.sundayHours,
										}}
									/>
									)
								</span>
							)}
						</p>
					)}
					{asbestosOnCallHours.hours > 0 && (
						<p className="ml-2 font-semibold">
							<Trans
								t={t}
								i18nKey="timeRegistration.asbestosOnCallHourSummary"
								count={asbestosOnCallHours.hours}
								values={{
									onCallHours: asbestosOnCallHours.hours,
								}}
							/>
							{asbestosOnCallHours.overtimeHours > 0 && (
								<span className="ml-2 font-semibold">
									(
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallOvertimeHourSummary"
										count={asbestosOnCallHours.overtimeHours}
										values={{
											onCallHours: asbestosOnCallHours.overtimeHours,
										}}
									/>
									)
								</span>
							)}
							{asbestosOnCallHours.eveningHours > 0 && (
								<span className="ml-2 font-semibold">
									(
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallEveningHourSummary"
										count={asbestosOnCallHours.eveningHours}
										values={{
											onCallHours: asbestosOnCallHours.eveningHours,
										}}
									/>
									)
								</span>
							)}
							{asbestosOnCallHours.nightHours > 0 && (
								<span className="ml-2 font-semibold">
									(
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallNightHourSummary"
										count={asbestosOnCallHours.nightHours}
										values={{
											onCallHours: asbestosOnCallHours.nightHours,
										}}
									/>
									)
								</span>
							)}
							{asbestosOnCallHours.sundayHours > 0 && (
								<span className="ml-2 font-semibold">
									(
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallSundayHourSummary"
										count={asbestosOnCallHours.sundayHours}
										values={{
											onCallHours: asbestosOnCallHours.sundayHours,
										}}
									/>
									)
								</span>
							)}
						</p>
					)}
				</div>
			) : (
				<>
					{showOnCallHours && (
						<div className="flex flex-col justify-end items-end">
							{onCallHours.hours > 0 && (
								<p className="ml-2 font-semibold">
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallHourSummary"
										count={onCallHours.hours}
										values={{
											onCallHours: onCallHours.hours,
										}}
									/>
									{onCallHours.overtimeHours > 0 && (
										<span className="ml-2 font-semibold">
											(
											<Trans
												t={t}
												i18nKey="timeRegistration.onCallOvertimeHourSummary"
												count={onCallHours.overtimeHours}
												values={{
													onCallOvertimeHours: onCallHours.overtimeHours,
												}}
											/>
											)
										</span>
									)}
								</p>
							)}
							{onCallbackHours.hours > 0 && (
								<p className="ml-2 font-semibold">
									<Trans
										t={t}
										i18nKey="timeRegistration.onCallbackHourSummary"
										count={onCallbackHours.hours}
										values={{
											onCallbackHours: onCallbackHours.hours,
										}}
									/>
									{onCallbackHours.overtimeHours > 0 && (
										<span className="ml-2">
											(
											<Trans
												t={t}
												i18nKey="timeRegistration.onCallbackOvertimeHourSummary"
												count={onCallbackHours.overtimeHours}
												values={{
													onCallbackOvertimeHours: onCallbackHours.overtimeHours,
												}}
											/>
											)
										</span>
									)}
								</p>
							)}
							{asbestosOnCallHours.hours > 0 && (
								<p className="ml-2 font-semibold">
									<Trans
										t={t}
										i18nKey="timeRegistration.asbestosOnCallHourSummary"
										count={asbestosOnCallHours.hours}
										values={{
											onCallHours: asbestosOnCallHours.hours,
										}}
									/>
									{asbestosOnCallHours.overtimeHours > 0 && (
										<span className="ml-2 font-semibold">
											(
											<Trans
												t={t}
												i18nKey="timeRegistration.onCallOvertimeHourSummary"
												count={asbestosOnCallHours.overtimeHours}
												values={{
													onCallOvertimeHours: asbestosOnCallHours.overtimeHours,
												}}
											/>
											)
										</span>
									)}
								</p>
							)}
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default OnCallSummarizer;

